import CloseIcon from "@mui/icons-material/Close";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
} from "react";
import AppLoader from "../../../components/AppLoader";
import Label from "../../../components/Label";
import TextInput from "../../../components/TextInputs";
import { CompanyRoutes } from "../../../routes";
import {
  createCompanyRole,
  updateCompanyRole,
} from "../../../services/company/roleApiFn";
import { handleOpenSnackBar } from "../../../utils";
import GlobalButton from "../../../components/CustomButton";
import { allIcons } from "../../../assets";
import { useSelector } from "react-redux";
import { t } from "i18next";

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialogContent-root": {
    padding: theme.spacing(2),
  },
  "& .MuiDialogActions-root": {
    padding: theme.spacing(2),
  },
  "& .MuiPaper-root": {
    width: "500px",
    [theme.breakpoints.down("sm")]: {
      margin: 12,
    },
  },
}));

let paramsData = {};

const AddEditCompanyRoleDialog = forwardRef(({}, ref) => {
  const [open, setOpen] = useState(false);
  const [companyRoutes, setCompanyRoutes] = useState([]);
  const [roleName, setRoleName] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const userData = useSelector((state) => state.userData.user);

  const [inputError, setInputError] = useState({
    roleName: false,
    pages: false,
  });

  useEffect(() => {
    let tempArray = CompanyRoutes.map((val) => {
      return { ...val, selected: false };
    });
    setCompanyRoutes(tempArray);
  }, []);

  useImperativeHandle(ref, () => ({
    openDialog(params) {
      setOpen(true);
      paramsData = params;
      if (params.type === "edit") {
        setRoleName(params.data.row.role);
        const updatedCompanyRoutes = companyRoutes.map((route) => {
          const findSelected = params.data.row.pages.find(
            (x) => x.name === route.name
          );
          return {
            ...route,
            selected: Boolean(findSelected),
          };
        });
        setCompanyRoutes(updatedCompanyRoutes);
      }
    },
  }));

  const handleClose = () => {
    setOpen(false);
    setInputError({});
    setRoleName("");
    let tempArray = CompanyRoutes.map((val) => {
      return { ...val, selected: false };
    });
    setCompanyRoutes(tempArray);
  };

  const handleAssignedPages = (val, ind) => {
    let tempArray = [...companyRoutes];
    tempArray[ind].selected = !tempArray[ind].selected;
    setCompanyRoutes(tempArray);
  };

  const handleAddEditRole = () => {
    if (!roleName.trim())
      return setInputError((prev) => ({ ...prev, roleName: true }));

    let selectedPages = companyRoutes.filter((s) => s.selected === true);

    if (selectedPages.length === 0)
      return handleOpenSnackBar("error", t("atLeastSelectOnePage"));

    let payload = {
      role: roleName,
      pages: selectedPages,
      companyId: userData?.adminId,
    };

    if (paramsData?.type === "add") {
      handleApiCall(createCompanyRole, payload, "");
    } else {
      handleApiCall(updateCompanyRole, payload, paramsData?.data?.row?._id);
    }
  };

  const handleApiCall = async (apiFunction, payload, id) => {
    try {
      setIsLoading(true);
      const response = await apiFunction(id, payload);
      if (response?.data?.status === "success") {
        handleOpenSnackBar("success", response?.data?.message);
        handleClose();
        paramsData?.getData();
      } else {
        handleOpenSnackBar("error", response?.data?.message);
      }
    } catch (error) {
      handleOpenSnackBar("error", t("somethingWentWrong"));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <AppLoader isVisible={isLoading} />
      <BootstrapDialog
        onClose={handleClose}
        aria-labelledby="customized-dialog-title"
        open={open}
      >
        <Stack direction={"row"} justifyContent={"space-between"}>
          <DialogTitle sx={{ m: 0, p: 2 }} id="customized-dialog-title">
            {paramsData.type === "add"
              ? t("newManagementRoles")
              : t("editManagementRoles")}
          </DialogTitle>

          <IconButton
            onClick={handleClose}
            sx={{
              position: "absolute",
              top: 5,
              right: 5,
            }}
          >
            <CloseIcon />
          </IconButton>
        </Stack>
        <DialogContent dividers>
          <Grid container spacing={2} mt={1}>
            <Grid item xs={12}>
              <Label required labelText={t("roleName")} htmlFor={"role-name"} />
              <TextInput
                required
                inputError={inputError.roleName}
                helperText={inputError.roleName && t("roleNameRequired")}
                id={"role-name"}
                placeholder={`${t("enter")} ${t("roleName")}`}
                value={roleName}
                name={"roleName"}
                onChange={(e) => {
                  setRoleName(e.target.value);
                  setInputError((prev) => ({ ...prev, roleName: false }));
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <Typography
                variant="h1"
                fontWeight={600}
                fontSize={"1rem"}
                textTransform={"capitalize"}
              >
                {t("allModules")}
              </Typography>
              <List>
                {companyRoutes.slice(0, -2).map((valll, _ind) => {
                  return (
                    <ListItem onClick={() => handleAssignedPages(valll, _ind)}>
                      <ListItemIcon sx={{ minWidth: 35 }}>
                        <img
                          src={
                            valll.selected
                              ? allIcons.checkSquareIcon
                              : allIcons.uncheckSquareIcon
                          }
                          alt="check-icon"
                          width={20}
                        />
                      </ListItemIcon>
                      <ListItemText secondary={t(valll.name)} />
                    </ListItem>
                  );
                })}
              </List>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <GlobalButton
            variant={"outlined"}
            btnText={"cancel"}
            styleProps={{ width: 100, height: 40 }}
            handleClickBtn={handleClose}
          />
          <GlobalButton
            variant={"contained"}
            btnText={paramsData.type === "add" ? t("addRoles") : t("editRoles")}
            styleProps={{ width: 150, height: 40 }}
            handleClickBtn={handleAddEditRole}
          />
        </DialogActions>
      </BootstrapDialog>
    </>
  );
});

export default AddEditCompanyRoleDialog;
